import React, { useEffect, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import axios from './axios-instance';
import 'react-toastify/dist/ReactToastify.css';
import { messages } from './translations';
import { getLangState } from './store/lang.slice';
import { useSelector, useDispatch } from 'react-redux';
import FontProvider from './components/FontProvider';
import {
  setBackgroundColor,
  setTextColor,
  setFormBackgroundColor,
  setFormTextColor,
  setTeamBackgroundColor,
  setTeamTextColor,
  setFormBtnColor,
  setHideDetails,
  setBorderColor,
  setSeparatorColor,
  setBtnColor,
  setBgAvailableDaysColor,
  setUnavailableDaysColor,
  setFontFamily,
  setIsInline,
} from './store/settings.slice';
import Routes from './routes/routes';

function App() {
  /* ------------------------------- Global State ------------------------------ */
  const lang = useSelector(getLangState);
  const dispatch = useDispatch();
  /* ------------------------------- Local State ------------------------------ */

  const queryClient = useMemo(() => new QueryClient(), []);
  /* --------------------------------- Effects -------------------------------- */
  useEffect(() => {
    axios.defaults.headers.common['Accept-Language'] = lang;
    queryClient.invalidateQueries();
    localStorage.setItem('lang', lang);
  }, [lang, queryClient]);

  useEffect(() => {
    const handleSettings = (event: any) => {
      const doc = document.getElementById('root');
      const app = document.getElementsByClassName('App')[0] as HTMLElement;
      if (doc === null) return;
      if (event.data.action === 'changeColor') {
        dispatch(setTextColor(event.data.color));
        dispatch(setBackgroundColor(event.data.bgColor));
        dispatch(setBtnColor(event.data.btnColor));
        if (event.data.formBackgroundColor) {
          dispatch(setFormBackgroundColor(event.data.formBackgroundColor));
        }
        if (event.data.formTextColor) {
          dispatch(setFormTextColor(event.data.formTextColor));
        }
        if (event.data.formBtnColor) {
          dispatch(setFormBtnColor(event.data.formBtnColor));
        }
        if (event.data.teamBackgroundColor) {
          dispatch(setTeamBackgroundColor(event.data.teamBackgroundColor));
        }
        if (event.data.teamTextColor) {
          dispatch(setTeamTextColor(event.data.teamTextColor));
        }
        dispatch(setHideDetails(event.data.hideDetails));
        dispatch(setBorderColor(event.data.borderColor));
        dispatch(setSeparatorColor(event.data.separatorColor));
        dispatch(setBgAvailableDaysColor(event.data.bgAvailableDaysColor));
        dispatch(setUnavailableDaysColor(event.data.unavailableDaysColor));
        dispatch(setIsInline(event.data.isInline));
        if (event.data.fontFamily) {
          dispatch(setFontFamily(event.data.fontFamily));
        }
      }
    };
    window.addEventListener('message', handleSettings);
    return () => {
      window.removeEventListener('message', handleSettings);
    };
  }, [queryClient]);

  return (
    <div className="App">
      <IntlProvider locale={lang} messages={messages[lang]}>
        <QueryClientProvider client={queryClient}>
          <MantineProvider withGlobalStyles withNormalizeCSS>
            <FontProvider>
              <Routes />
            </FontProvider>
          </MantineProvider>
        </QueryClientProvider>
      </IntlProvider>
    </div>
  );
}

export default App;
